import { Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, switchMap, startWith, takeUntil, map } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AutocompleteOption, Filter, SearchField } from '../filter';

@Component({
    selector: 'ts-universal-search-bar',
    templateUrl: './universal-search-bar.component.html',
    styleUrls: ['./universal-search-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UniversalSearchBarComponent implements OnDestroy {
  @Input() fields: SearchField[] = [];

  @Output() optionSelected = new EventEmitter<Filter>();

  private readonly destroy$ = new Subject<void>();

  public readonly inputControl = new FormControl('');

  public readonly options$ = this.inputControl.valueChanges.pipe(
    startWith(''),
    debounceTime(200),
    switchMap((query) => {
      const safeQuery = query || '';
      const parsed = this.parseInput(safeQuery);

      let operatorPart: string | undefined;

      let operators: string[] = [];
      let values: Observable<AutocompleteOption[]>;
      let fieldName: string;
      let operator: string;

      switch (true) {
        case !parsed.field:
          return of(
            this.fields
              .map((f) => ({ value: this.translate.instant(f.name), enabled: true }))
              .filter((label) => label.value.toLowerCase().includes(safeQuery.toLowerCase()))
          );

        case !!parsed.field && !parsed.operator:
          fieldName = this.translate.instant(parsed.field.name);

          operatorPart = safeQuery.split(' ')[1] || '';

          operators = parsed.field.operators
            .filter((op) => op.toLowerCase().includes(operatorPart.toLowerCase()))
            .map((op) => `${fieldName} ${op}`);

          return of(operators.map((op) => ({ value: op, enabled: true })));
        case !!parsed.field && !!parsed.operator:
          fieldName = this.translate.instant(parsed.field.name);
          operator = parsed.operator;

          values = !parsed.field?.autocompleteSource
            ? of([{ value: parsed.value ?? '', enabled: true }])
            : parsed.field.autocompleteSource(parsed.value);

          return values.pipe(
            map((values) =>
              values.map((value) => ({ value: `${fieldName} ${operator} ${value.value}`, enabled: value.enabled }))
            )
          );
        default:
          return of([]);
      }
    }),
    takeUntil(this.destroy$)
  );

  constructor(private readonly translate: TranslateService) {}

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public selectOption(option: string) {
    const parsed = this.parseInput(option);

    const translatedOption = this.translate.instant(option);

    const searchValue =
      !parsed.operator && parsed.field?.operators?.length === 1
        ? translatedOption + ' ' + parsed.field.operators[0] + ' '
        : translatedOption + ' ';

    this.inputControl.setValue(searchValue);

    if (!parsed.field || !parsed.operator || !parsed.value) return;

    this.optionSelected.emit(parsed);

    this.inputControl.setValue('');
  }

  private parseInput(input: string): Filter {
    const result: Filter = {
      field: null,
      operator: null,
      value: null,
    };

    const trimmed = input.trim();

    const field = this.fields.find((f) => input.startsWith(this.translate.instant(f.name)));

    if (!field) return result;

    result.field = field;

    const fieldlessInput = trimmed.replace(this.translate.instant(field.name), '').trim();

    const operator = field.operators.find((o) => fieldlessInput.startsWith(o));

    if (!operator) return result;

    result.operator = operator;

    const value = fieldlessInput.replace(operator, '').trim();

    if (!value) return result;

    result.value = value;

    return result;
  }
}
